/**
 * Event Status Utility Functions
 *
 * Calculates event status (Ongoing/Ended) and duration based on:
 * - Rolling window per model type
 * - Recording timestamps
 * - Current time
 */

// Rolling window durations per model (in milliseconds)
// Source: human-validated-events-callback.js (lines 98-113)
export const EVENT_ROLLING_WINDOWS_MS: Record<string, number> = {
  gasleak: 6 * 60 * 60 * 1000, // 6 hours
  liquidleak: 6 * 60 * 60 * 1000, // 6 hours
  hardhat: 15 * 60 * 1000, // 15 minutes
  fire: 25 * 60 * 1000, // 25 minutes
  smoke: 25 * 60 * 1000, // 25 minutes
};

const DEFAULT_ROLLING_WINDOW_MS = 30 * 60 * 1000; // 30 minutes

// Values (runtime)
export const EVENT_STATUSES = ["Ongoing", "Ended"] as const;

// Type derived from values
export type EventStatus = (typeof EVENT_STATUSES)[number];

// Dropdown options
export const STATUS_OPTIONS = [
  ...EVENT_STATUSES.map(s => ({ title: s, value: s })),
];

// Gets the rolling window duration for a given model type
export const getRollingWindowMs = (modelId: string): number => {
  if (process.env.ENVIRONMENT_NAME === "dev") {
    return 60 * 1000 * 5; // 5 mins for dev
  }

  const normalizedModel = modelId.toLowerCase();

  return EVENT_ROLLING_WINDOWS_MS[normalizedModel] ?? DEFAULT_ROLLING_WINDOW_MS;
};

/**
 * Calculates whether an event is ongoing or ended
 *
 * @param {string} newestRecordingTimestamp - ISO timestamp of the most recent recording
 * @param {string} modelId - The model type (e.g., "GASLEAK", "FIRE")
 * @param {number} currentTime - Optional current time for testing (defaults to Date.now())
 * @return {EventStatus} "Ongoing" or "Ended"
 */
export const getEventStatus = (
  newestRecordingTimestamp: string,
  modelId: string,
  currentTime: number = Date.now()
): EventStatus => {
  // Compare the sum of the most recent timestamp and the rolling window to the currentTime
  const mostRecentTimeStampMs = new Date(newestRecordingTimestamp).getTime();
  const rollingWindowTimeMs = getRollingWindowMs(modelId);
  const expirationTimeMs = mostRecentTimeStampMs + rollingWindowTimeMs;

  // if the current time is before the expiry time, the event is ongoing
  return currentTime < expirationTimeMs ? "Ongoing" : "Ended";
};

/**
 * Formats duration in milliseconds to a human-readable string
 * e.g., "6h 32min", "45min", "2min"
 * @param {number} durationMs - Duration in milliseconds
 * @return {string} Human-readable duration
 * @return {string} "0min" for durations <= 0
 */
export const formatDuration = (durationMs: number): string => {
  if (durationMs <= 0) return "0min";

  const totalMinutes = Math.floor(durationMs / (1000 * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
  }

  return `${minutes}min`;
};

/**
 * Calculates event duration from oldest to newest timestamp
 * Returns null if fewer than 2 recordings
 *
 * @param {string} eventTimestamp - ISO timestamp of the event (first detection)
 * @param {string} newestRecordingTimestamp - ISO timestamp of the most recent recording
 * @param {number} recordingsCount - Number of recordings for this event
 * @return {string | null} Formatted duration or null if insufficient data
 */
export const calculateEventDuration = (
  eventTimestamp: string,
  newestRecordingTimestamp: string,
  recordingsCount: number
): string | null => {
  if (recordingsCount < 2) return null;

  const oldestTimeStampMs = new Date(eventTimestamp).getTime();
  const newestTimeStampMs = new Date(newestRecordingTimestamp).getTime();
  const durationMs = newestTimeStampMs - oldestTimeStampMs;

  return formatDuration(durationMs);
};
/**
 * Combined helper that returns both status and duration for an event
 * Use this when you need both values to avoid redundant calculations
 */

export interface EventStatusInfo {
  status: EventStatus;
  duration: string | null;
}

export const getEventStatusInfo = (
  eventTimestamp: string,
  newestRecordingTimestamp: string,
  recordingsCount: number,
  modelId: string,
  currentTime: number = Date.now()
): EventStatusInfo => {
  const status = getEventStatus(newestRecordingTimestamp, modelId, currentTime);
  const duration = calculateEventDuration(
    eventTimestamp,
    newestRecordingTimestamp,
    recordingsCount
  );

  // return the current status, duration, and ongoing flag
  return {
    status: status,
    duration: duration,
  };
};
